import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridWrapper } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import React, { FC } from "react";
import { css } from "styled-components";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import ADIColors from "@NA/Utils/ADIColors";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import siteMessage from "@insite/client-framework/SiteMessage";
import { TokenizationDisplayInfo } from "./ADICCTokenizationModel";
import { convertCardTypeToName } from "@NA/Utils/Utility";

interface OwnProps {
    modalOpen: boolean;
    customerData: TokenizationDisplayInfo;
    handleResponse: (selectionStatus: boolean) => void;
}

export interface ADIProductListVisualFilterModalStyles {
    ccTokenModal?: ModalPresentationProps;
    heading?: TypographyPresentationProps;
    ccPara?: TypographyPresentationProps;
    ccUserInfo?: InjectableCss;
    ccUserInfoPara?: InjectableCss;
    ccDetails?: InjectableCss;
    ccDetailsPara?: TypographyPresentationProps;
    ccDetailsWrapper?: InjectableCss;
    ccNote?: TypographyPresentationProps;
    ccActionContainerGridItem?: GridItemProps;
    primaryButton?: ButtonPresentationProps;
    secondaryButton?: ButtonPresentationProps;
}

export const adiProductListVisualFilterModalStyles: ADIProductListVisualFilterModalStyles = {
    ccPara: {
        css: css`
            font-size: 14px;
            margin-bottom: 18px;
            color: ${ADIColors.darkBlack2};
            line-height: normal;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 15px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    ccUserInfo: {
        css: css`
            font-size: 14px;
            margin-bottom: 18px;
            color: ${ADIColors.black};
            line-height: normal;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 25px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    ccUserInfoPara: {
        css: css`
            font-size: 14px;
            margin-bottom: 0px;
            color: ${ADIColors.black};
            line-height: normal;
        `,
    },
    ccDetailsWrapper: {
        css: css`
            margin-bottom: 17px;
            padding-bottom: 17px;
            border-bottom: 1px solid ${ADIColors.gray2};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 25px;
                            padding-bottom: 24px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    ccDetails: {
        css: css`
            font-size: 14px;
            line-height: normal;
            list-style: disc;
            padding-left: 20px;
            margin-left: 30px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-left: 20px;
                            margin-left: 5px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    ccDetailsPara: {
        css: css`
            font-size: 14px;
            margin-bottom: 0px;
            color: ${ADIColors.black};
            line-height: normal;
        `,
    },
    ccNote: {
        css: css`
            font-size: 14px;
            margin-bottom: 40px;
            color: ${ADIColors.black};
            line-height: normal;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 25px;
                    `,
                    css`
                        margin-bottom: 25px;
                    `,
                    css`
                        margin-bottom: 18px;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    ccActionContainerGridItem: {
        css: css`
            display: flex;
            flex-basis: 100%;
            max-width: 100%;
            align-items: center;
            flex-direction: row;
            justify-content: flex-end;
            margin-top: 22px;
            padding-bottom: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-wrap: wrap;
                        flex-direction: column-reverse;
                        gap: 18px;
                        align-items: center;
                        justify-content: center;
                        padding-bottom: 0px;
                        flex-direction: row;
                        flex-wrap: nowrap;
                        margin-top: 0px;
                    `,
                    css`
                        flex-wrap: wrap;
                        flex-direction: column-reverse;
                        gap: 18px;
                        align-items: center;
                        justify-content: center;
                        padding-bottom: 0px;
                        flex-direction: row;
                        flex-wrap: nowrap;
                        margin-top: 0px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    primaryButton: {
        css: css`
            min-width: 160px;
            height: 42px;
            min-height: 42px;
            margin: 0 0 0 30px;
            border-radius: 4px;
            padding: 0 10px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            border-width: 1px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        height: 35px;
                        min-height: 35px;
                        margin: 0;
                        min-width: initial;
                        flex-basis: 50%;
                    `,
                    css`
                        font-size: 14px;
                        height: 35px;
                        min-height: 35px;
                        margin: 0;
                        min-width: initial;
                        flex-basis: 50%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    secondaryButton: {
        css: css`
            min-width: 160px;
            height: 42px;
            min-height: 42px;
            font-size: 16px;
            font-weight: bold;
            padding: 0 10px;
            text-align: center;
            border-radius: 4px;
            margin: 0;
            border-width: 1px;
            &:hover {
                background: ${ADIColors.darkBlue6} !important;
                color: ${ADIColors.blue1} !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px;
                        height: 35px;
                        min-height: 35px;
                        margin: 0;
                        min-width: initial;
                        flex-basis: 50%;
                    `,
                    css`
                        font-size: 14px;
                        height: 35px;
                        min-height: 35px;
                        margin: 0;
                        min-width: initial;
                        flex-basis: 50%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    ccTokenModal: {
        headlineTypographyProps: {
            variant: "h2",
            css: css`
                font-size: 34px;
                font-weight: 900;
                font-stretch: normal;
                font-style: normal;
                line-height: 36px;
                letter-spacing: normal;
                color: ${ADIColors.darkBlue1};
                border: 0;
                margin-top: 0;
                margin-bottom: 10px;
                padding-bottom: 0;
            `,
        },

        closeButtonProps: {
            css: css`
                display: none; // close button hidden
                padding: 0;
                margin-top: -40px;
                margin-right: -25px;
                &:hover {
                    background: transparent;
                    border-color: transparent;
                }
                > ${IconWrapper} {
                    color: ${getColor("primary")};
                    width: 35px;
                    height: 35px;
                    > svg {
                        width: 35px;
                        height: 35px;
                        stroke-width: 1.4px;
                    }
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            margin-right: -25px;
                            margin-top: 0px;
                        `,
                        css`
                            margin-right: -25px;
                            margin-top: 0px;
                        `,
                        css`
                            margin-right: -25px;
                            margin-top: 0px;
                        `,
                        null,
                        null,
                    ]);
                }}
            `,
        },
        cssOverrides: {
            headlineTypography: css`
                font-size: 28px;
                line-height: normal;
                margin-bottom: 19px;
                font-weight: bold;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 28px;
                            margin-bottom: 15px;
                        `,
                        css`
                            font-size: 28px;
                            margin-bottom: 15px;
                        `,
                        css`
                            font-size: 28px;
                            margin-bottom: 15px;
                        `,
                        css`
                            font-size: 28px;
                        `,
                        css`
                            font-size: 28px;
                        `,
                    ]);
                }}
            `,
            modalBody: css`
                padding: 29px 28px 28px 28px;
                border-radius: 6px;
                overflow: hidden;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            max-width: 100%;
                            padding: 35px 20px 30px 20px;
                            top: 0%;
                            position: absolute;
                            height: auto;
                            bottom: 0%;
                            border-radius: 0px;
                        `,
                        css`
                            max-width: 100%;
                            padding: 35px 20px 30px 20px;
                            top: 0%;
                            position: absolute;
                            height: auto;
                            bottom: 0%;
                            border-radius: 0px;
                        `,
                        css`
                            max-width: 90%;
                            padding: 29px 28px 28px 28px;
                            top: 8%;
                            position: absolute;
                            height: auto;
                        `,
                        css`
                            max-width: 526px;
                            top: 15%;
                            position: absolute;
                            height: auto;
                            margin: 0 auto;
                            max-height: 80vh;
                        `,
                        css`
                            max-width: 526px;
                            top: 15%;
                            position: absolute;
                            height: auto;
                            margin: 0 auto;
                            max-height: 80vh;
                        `,
                    ]);
                }}
                ${GridWrapper} {
                    max-width: 100%;
                }
            `,

            modalContent: css`
                padding: 0px;
                overflow: hidden auto;
                padding-right: 3px;

                /* Target only Firefox */
                @-moz-document url-prefix() {
                    scrollbar-width: thin;
                    scrollbar-color: ${ADIColors.lightblue2} ${ADIColors.gray6};
                }

                &::-webkit-scrollbar {
                    width: 8px;
                }

                &::-webkit-scrollbar-track {
                    background: ${getColor("common.accent")};
                }

                &::-webkit-scrollbar-thumb {
                    background-color: ${ADIColors.lightblue2};
                    border-radius: 7px;
                }
            `,
            modalTitle: css`
                border: unset;
                padding: 0px 10px 0px 0px;
            `,
        },
    },
};

const styles = adiProductListVisualFilterModalStyles;

const ADICCTokeCoreModel: FC<OwnProps> = ({ modalOpen, customerData, handleResponse }) => {
    const getcreditCardList = () => {
        return (
            <>
                {customerData &&
                    customerData.cardList &&
                    customerData.cardList.length >= 1 &&
                    customerData.cardList.map((card, index) => (
                        <StyledWrapper as="ul" key={card.id || index} {...styles.ccDetails}>
                            <Typography as="li" {...styles.ccDetailsPara}>
                                {translate(
                                    `${convertCardTypeToName(
                                        card.cardType,
                                    ).toUpperCase()} **** **** **** ${card.cardNumber.slice(-4)} exp. ${
                                        card.expiryMonth
                                    }/${card.expiryYear}`,
                                )}
                            </Typography>
                        </StyledWrapper>
                    ))}
            </>
        );
    };

    return (
        <Modal
            isOpen={modalOpen}
            isCloseable={false}
            headline={translate("Credit Card Authorization")}
            {...styles.ccTokenModal}
            onClick={e => e.stopPropagation()}
        >
            <StyledWrapper>
                <Typography as="p" {...styles.ccPara}>
                    {siteMessage("CCTokenizationCardPopupText1")}
                </Typography>
                <Typography as="p" {...styles.ccPara}>
                    {siteMessage("CCTokenizationCardPopupText2")}
                </Typography>
                <StyledWrapper {...styles.ccUserInfo}>
                    <Typography as="p" {...styles.ccUserInfoPara}>
                        {translate("User: ")}
                        <Typography as="b">{customerData.userName}</Typography>
                    </Typography>
                    <Typography as="p" {...styles.ccUserInfoPara}>
                        {translate("Customer: ")}
                        <Typography as="b">{customerData.customerName}</Typography>
                    </Typography>
                    <Typography as="p" {...styles.ccUserInfoPara}>
                        {translate("Account Number: ")}
                        <Typography as="b">{customerData.accountNumber}</Typography>
                    </Typography>
                    <Typography as="p" {...styles.ccUserInfoPara}>
                        {translate("Date Signed: ")}
                        <Typography as="b">{customerData.dateSigned}</Typography>
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.ccDetailsWrapper}>{getcreditCardList()}</StyledWrapper>

                <Typography as="p" {...styles.ccNote}>
                    <Typography as="b">{translate("Note: ")}</Typography>
                    {siteMessage("CCTokenizationCardPopupText3")}
                </Typography>

                <StyledWrapper {...styles.ccActionContainerGridItem}>
                    <Button variant="secondary" {...styles.secondaryButton} onClick={() => handleResponse(false)}>
                        {translate("Disagree")}
                    </Button>
                    <Button variant="primary" {...styles.primaryButton} onClick={() => handleResponse(true)}>
                        {translate("Agree")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default ADICCTokeCoreModel;
